import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { User, Linkedin } from "lucide-react";
import axios from "axios";
import { BilingualEyebrow, BilingualHeading } from "@/components/ui/bilingual-heading";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Hungarian name override map: HU convention puts family name first.
   Source-of-truth `name` field uses English convention (given-family). */
const nameHuMap = {
  "Dr. Roland Hetényi":       "Dr. Hetényi Roland",
  "Dr. Dániel Hanna":         "Dr. Hanna Dániel",
  "Péter Szabó":              "Szabó Péter",
  "Zoltán Kopasz":            "Kopasz Zoltán",
  "Gréta Varga":              "Varga Gréta",
  "dr. Tibor Héjj":           "dr. Héjj Tibor",
  "dr. János Matuz":          "dr. Matuz János",
  "Balázs Antal Somogyi":     "Somogyi Balázs Antal",
  "Dr. Gábor Kemenesi":       "Dr. Kemenesi Gábor",
};

function displayName(name, lang) {
  if (lang === "hu" && nameHuMap[name]) return nameHuMap[name];
  return name;
}

const linkedinMap = {
  "Roland": "https://www.linkedin.com/in/hetenyiroland/",
  "Szab": "https://www.linkedin.com/in/peter-szabo292/",
  "Kopasz": "https://www.linkedin.com/in/zolt%C3%A1n-kopasz-890a69228/",
  "Somogyi": "https://www.linkedin.com/in/bal%C3%A1zs-somogyi-b66941356/",
  "Tibor": "https://www.linkedin.com/in/tiborhejj/",
  "Kemenesi": "https://www.linkedin.com/in/g%C3%A1bor-kemenesi-630867137/",
};

const fallbackTeam = [
  { id: "1", name: "Dr. Roland Het\u00e9nyi", role: "CEO, Medical Doctor", expertise: "Virology, UV Inactivation Technology, Biosafety", bio: "Medical doctor and pioneer in UV-based virus inactivation. Founded RoLink to bridge the gap between high-containment virology and scalable biodefense. Secured 350,000 EUR COVID Fund grant and leads IP development (PCT/HU2025/050010).", image_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/g63lfqln_Hetenyi%20Roland.jpg" },
  { id: "2", name: "Dr. D\u00e1niel Hanna", role: "Co-owner, Medical Doctor", expertise: "Vaccine Development, Purification, Biodefense", bio: "Medical doctor specializing in vaccine development and virus purification. Co-developed RoLink's proprietary anion exchange chromatography method achieving 75.5% RNA recovery rates.", image_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/nb1eyaie_Hanna%20Daniel%20new%20profile%20picture.jpg" },
  { id: "3", name: "P\u00e9ter Szab\u00f3", role: "Researcher", expertise: "Scientific Writing, Systematic Reviews, Meta-Analysis", bio: "Specializes in scientific writing, systematic reviews, meta-analysis, and biosafety-focused biomedical research. His work focuses on evidence synthesis, scientific communication, and translational research strategy.", image_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/e45f7rot_P%C3%A9ter%20Szab%C3%B3%20Science%20Writer%2C%20Systematic%20Review%20and%20Meta%20analysis.jpg" },
  { id: "4", name: "Zolt\u00e1n Kopasz", role: "Researcher", expertise: "Virology, Antivirals, Inactivation Specialist", bio: "Biologist working in virology, antiviral research, and viral inactivation technologies, with a particular interest in Zika virus research and emerging viral pathogens.", image_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/3rpf87df_Zolt%C3%A1n%20Kopasz%2C%20Virologist%2C%20Antivirals%2C%20Inactivation%20Specialist.jpg" },
  { id: "5", name: "Gr\u00e9ta Varga", role: "Researcher", expertise: "Virology, Industrial Biotechnology, Hantavirus", bio: "Biotechnology researcher specializing in virology and applied industrial biotechnology. Her work focuses on hantavirus isolation and characterization, viral inactivation technologies, antiviral research, and the development of innovative validation systems supporting next-generation biotechnological products.", image_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/mtj3fi50_Varga%20Gr%C3%A9ta.jpg" },
  { id: "6", name: "dr. Tibor H\u00e9jj", role: "Board Member", expertise: "Innovation Management, IP Strategy", bio: "Experienced innovation strategist with deep expertise in intellectual property management, technology transfer, and strategic advisory for deep-tech ventures in the life sciences sector.", image_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/guej0d1c_Tibor%20H%C3%A9jj.jpg" },
  { id: "7", name: "dr. J\u00e1nos Matuz", role: "Board Member", expertise: "Regulatory Strategy", bio: "Senior advisor with extensive experience in biodefense policy, biosafety regulatory frameworks, and institutional governance for biotechnology organizations operating within national security and public health domains.", image_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/oqzm4zbr_dr.%20J%C3%A1nos%20Matuz.png" },
  { id: "8", name: "Bal\u00e1zs Antal Somogyi", role: "Chief Biosafety Advisor", expertise: "Biosafety, Epidemic Preparedness, Strategic Collaboration", bio: "Serves as Chief Biosafety Advisor for RoLink Biotechnology's pandemic defense development project focused on UV inactivation technologies. With expertise in biosafety, epidemic preparedness, and strategic scientific collaboration, he supports the project through professional advisory work, international networking, and the development of research and industrial partnerships.", image_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/27oxcgl9_Bal%C3%A1zs%20Antal%20Somogyi.jpg" },
  { id: "9", name: "Dr. G\u00e1bor Kemenesi", role: "Senior Advisor & Virologist", expertise: "Emerging Viruses, Zoonotic Transmission, Pandemic Preparedness", bio: "Dr. G\u00e1bor Kemenesi serves as mentor and scientific advisor to RoLink Biotechnology Kft. within the framework of the NKFIH-funded project \"V\u00e9delmi k\u00e9pess\u00e9gfejleszt\u00e9s a j\u00e1rv\u00e1nyok kor\u00e1ban: UV-inaktiv\u00e1ci\u00f3\" (Project ID: 2023-1.1.2-GYORS\u00cdT\u00d3S\u00c1V-2024-00020). As a member of the National Innovation Agency's (NI\u00dc) Mentor Program, he provides expert support in virology, contributing to the successful implementation of the project and its scientific and innovation objectives. In his mentoring role, Dr. Kemenesi provides ongoing scientific consultation, strategic guidance, and professional support to the RoLink team throughout the project period. His expertise strengthens the company's research and development activities related to UV-based viral inactivation technologies and pandemic preparedness solutions.", image_url: "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/paw5u58x_G%C3%A1bor%20Kemenesi%20New.jpg" },
];

function getLinkedIn(name) {
  for (const [key, url] of Object.entries(linkedinMap)) {
    if (name && name.includes(key)) return url;
  }
  return null;
}

function TeamImage({ src, name }) {
  if (src) {
    return <img src={src} alt={name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" loading="lazy" />;
  }
  return (
    <div className="w-full h-full flex items-center justify-center bg-[#111111]">
      <User size={28} className="text-[#374151]" />
    </div>
  );
}

function LinkedInBtn({ url, name }) {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${name} LinkedIn profile`}
      className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 border border-[#1F2937] hover:border-[#0A66C2] text-[#6B7280] hover:text-[#0A66C2] transition-colors duration-300 rounded-sm text-xs focus:outline-none focus:ring-2 focus:ring-[#0A66C2]/30"
    >
      <Linkedin size={13} />
      <span className="font-mono">LinkedIn</span>
    </a>
  );
}

function MemberCard({ member, size = "sm" }) {
  const { lang } = useLanguage();
  const linkedin = getLinkedIn(member.name);
  const imgSize = size === "lg" ? "w-20 h-20" : "w-16 h-16";
  const nameSize = size === "lg" ? "text-xl" : "text-base";
  const shownName = displayName(member.name, lang);

  return (
    <div className="flex flex-col items-center text-center">
      <div className={`${imgSize} rounded-full overflow-hidden shrink-0 border border-[#1F2937] mb-4`}>
        <TeamImage src={member.image_url} name={member.name} />
      </div>
      <h3 className={`font-heading ${nameSize} font-bold text-[#F3F4F6] mb-1`}>{shownName}</h3>
      <p className="text-xs text-[#EE1D25] font-medium mb-1">{member.role}</p>
      <p className="text-[10px] text-[#9CA3AF] font-mono mb-3">{member.expertise}</p>
      <p className={`${size === "lg" ? "text-sm" : "text-xs"} text-[#9CA3AF] leading-relaxed`}>{member.bio}</p>
      <LinkedInBtn url={linkedin} name={member.name} />
    </div>
  );
}

export default function Team() {
  const [team, setTeam] = useState(fallbackTeam);

  useEffect(() => {
    axios.get(`${API}/team`).then(r => {
      if (r.data?.length) setTeam(r.data);
    }).catch(() => {});
  }, []);

  const leadership = team.filter(m => m.role && (m.role.includes("CEO") || m.role.includes("Co-owner")));
  const researchers = team.filter(m => m.role === "Researcher");
  const board = team.filter(m => m.role && (m.role.includes("Board") || m.role.includes("Advisor")));

  return (
    <section id="team" data-testid="team-section" className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#EE1D25]/4 rounded-full blur-[150px]" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-16">
          <BilingualEyebrow en="Team &amp; Board" hu="Csapat &eacute;s vezet&#337;s&eacute;g" className="text-[#9CA3AF]" />
          <BilingualHeading en="Scientific Team" hu="Tudom&aacute;nyos csapat" colorClass="text-[#F3F4F6]" />
        </motion.div>

        {/* Leadership */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F2937] mb-px">
          {leadership.map((member, i) => (
            <motion.div key={member.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-[#050505] p-8 md:p-10 group" data-testid={`team-card-${i}`}>
              <MemberCard member={member} size="lg" />
            </motion.div>
          ))}
        </div>

        {/* Researchers */}
        {researchers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1F2937] mt-px">
            {researchers.map((member, i) => (
              <motion.div key={member.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i + 2) * 0.1 }} className="bg-[#050505] p-7 md:p-8 group" data-testid={`team-card-${i + 2}`}>
                <MemberCard member={member} />
              </motion.div>
            ))}
          </div>
        )}

        {/* Board */}
        {board.length > 0 && (
          <>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF] font-mono font-medium mt-16 mb-6 text-center">Advisory Board</motion.p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1F2937] max-w-4xl mx-auto">
              {board.map((member, i) => (
                <motion.div key={member.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-[#050505] p-7 md:p-8 group" data-testid={`board-card-${i}`}>
                  <MemberCard member={member} />
                </motion.div>
              ))}
            </div>
          </>
        )}

        {/* Company LinkedIn */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mt-10 text-center">
          <a
            href="https://www.linkedin.com/company/rolinkbiotechnology/posts/?feedView=all"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="RoLink Biotechnology LinkedIn page"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#1F2937] hover:border-[#0A66C2] text-[#6B7280] hover:text-[#0A66C2] transition-colors duration-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#0A66C2]/30"
            data-testid="company-linkedin"
          >
            <Linkedin size={16} />
            <span>Follow RoLink on LinkedIn</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
